import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { Lesson } from '../evaluator/types.ts';
import { loadLessonContext } from '../content/load.ts';
import { situacaoDaLicao } from '../content/estado.ts';
import { useLessonProgress } from '../state/lessonProgress.ts';
import { useDesbloqueioTotal } from '../state/desbloqueio.ts';
import { Button, Callout } from '../components/primitives/index.tsx';
import { EstadoACarregar } from '../components/brand/LoadingMark.tsx';
import { t } from '../i18n/index.ts';
import './modulo.css';

/**
 * A importação é dinâmica de propósito: é o que mantém o CodeMirror e o
 * avaliador fora do pacote inicial e o orçamento de RNF-001 cumprido.
 * Ver ADR 0002. Uma importação estática aqui quebra a compilação no
 * verificador de orçamento, e é isso que se quer que aconteça.
 */
const LessonRunner = lazy(() =>
  import('../components/lesson/LessonRunner.tsx').then((m) => ({ default: m.LessonRunner })),
);

export function LessonRoute() {
  const { lessonId } = useParams({ from: '/licao/$lessonId' });
  const navigate = useNavigate();
  const [contexto, setContexto] = useState<{ lesson: Lesson; todas: Lesson[]; trilha: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const licoes = useLessonProgress((s) => s.lessons);
  const concluidas = useMemo(
    () => new Set(Object.entries(licoes).filter(([, e]) => e.completedAt).map(([id]) => id)),
    [licoes],
  );
  const desbloqueado = useDesbloqueioTotal();

  useEffect(() => {
    const controlador = new AbortController();
    setContexto(null);
    setErro(null);
    loadLessonContext(lessonId, controlador.signal)
      .then(setContexto)
      .catch((err: unknown) => {
        if (controlador.signal.aborted) return;
        setErro(err instanceof Error ? err.message : String(err));
      });
    return () => controlador.abort();
  }, [lessonId]);

  if (erro) {
    return (
      <div className="pagina-modulo">
        <Callout tone="error" role="alert">
          {t('trilhas.erro.titulo')}
        </Callout>
      </div>
    );
  }

  if (!contexto) {
    return (
      <div className="pagina-modulo">
        <EstadoACarregar texto={t('trilhas.acarregar')} />
      </div>
    );
  }

  // Sair de uma lição devolve à página do curso, com a secção dela aberta —
  // não à raiz. Quem entrou pela lista quer voltar à lista: mandá-lo para o
  // catálogo obriga-o a encontrar outra vez o sítio onde já estava (ADR 0027).
  const voltarAoModulo = () =>
    void navigate({
      to: '/curso/$trackId',
      params: { trackId: contexto.trilha },
      search: { seccao: contexto.lesson.module },
    });

  // O desbloqueio é verificado aqui e não só na lista: sem isto, o endereço
  // directo contorna a sequência pedagógica que o conteúdo declara (RF-014).
  // E é por isso que a conta de acesso total também tem de passar por aqui —
  // quem revê conteúdo chega a uma lição por endereço tantas vezes como pelo
  // caminho, e um destrancar que só valesse na lista dava uma aplicação em que
  // o nó abre e a página seguinte recusa.
  const { estado, faltam } = situacaoDaLicao(
    contexto.lesson,
    contexto.todas,
    concluidas,
    desbloqueado,
  );
  if (estado === 'bloqueada') {
    return (
      <div className="pagina-modulo">
        <Callout tone="info" role="status">
          <p>{t('licao.bloqueada.titulo')}</p>
          <p>{t('trilhas.precisaDe', { licoes: faltam.join(', ') })}</p>
        </Callout>
        <Button onClick={voltarAoModulo}>{t('licao.bloqueada.accao')}</Button>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="pagina-modulo">
          <EstadoACarregar texto={t('trilhas.acarregar')} />
        </div>
      }
    >
      <LessonRunner lesson={contexto.lesson} onExit={voltarAoModulo} />
    </Suspense>
  );
}
